import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, UserCircle2, Building2, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";

/** HOD Assignments — assign multiple hospitals/locations to each HOD-role user so
 *  their scope on Dashboard / Reports / Employees expands beyond a single site.
 */
export default function HodAssignmentsTab() {
  const [rows, setRows] = useState(null); // null = loading
  const [locs, setLocs] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState({});

  async function load() {
    const [r, l] = await Promise.all([
      api.get("/masters/hod-assignments"),
      api.get("/masters/locations"),
    ]);
    setRows(r.data);
    setLocs(l.data || []);
  }
  useEffect(() => { load(); }, []);

  const locOptions = useMemo(
    () => locs.map(l => ({ id: l.id, name: `${l.name}${l.code ? ` (${l.code})` : ""}` })),
    [locs]
  );

  const filtered = useMemo(() => {
    if (!rows) return [];
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter(r =>
      (r.name || "").toLowerCase().includes(s) ||
      (r.emp_id || "").toLowerCase().includes(s) ||
      (r.email || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  async function save(row, ids) {
    setSaving(s => ({ ...s, [row.emp_db_id]: true }));
    try {
      await api.put(`/masters/hod-assignments/${row.emp_db_id}`, { location_ids: ids });
      toast.success(`Updated locations for ${row.name}`);
      setRows(rs => rs.map(x => x.emp_db_id === row.emp_db_id ? { ...x, hod_location_ids: ids } : x));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(s => ({ ...s, [row.emp_db_id]: false }));
    }
  }

  return (
    <Card className="border-slate-200 rounded-2xl">
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-slate-900 font-bold">Multi-location HOD scope</div>
            <p className="text-xs text-slate-500 mt-0.5">HODs listed here will see employees from every hospital/location you tick — instead of only their primary site.</p>
          </div>
          <div className="relative min-w-[240px]">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search HOD…" className="pl-9" data-testid="hod-search"/>
          </div>
        </div>

        {rows === null ? (
          <div className="flex items-center gap-2 text-slate-500 py-10 justify-center"><Loader2 className="h-4 w-4 animate-spin"/> Loading HODs…</div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-slate-400">
            <UserCircle2 className="h-8 w-8 mx-auto mb-2 text-slate-300"/>
            No HODs yet. Designate a Head of Department on the Departments tab first.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(row => (
              <HodRow key={row.emp_db_id} row={row} locOptions={locOptions} saving={saving[row.emp_db_id]} onSave={save}/>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function HodRow({ row, locOptions, saving, onSave }) {
  const [selected, setSelected] = useState(row.hod_location_ids || []);
  const changed = JSON.stringify([...selected].sort()) !== JSON.stringify([...(row.hod_location_ids || [])].sort());

  return (
    <div className="p-4 rounded-2xl border border-slate-200 bg-white space-y-3" data-testid={`hod-row-${row.emp_db_id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-11 w-11 rounded-xl bg-sky-100 text-sky-700 grid place-items-center flex-shrink-0">
          <UserCircle2 className="h-5 w-5"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-bold text-slate-900">{row.name}</div>
          <div className="text-xs text-slate-500 mt-0.5 font-mono">{row.emp_id} · {row.email || "no email"}</div>
          {row.departments?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {row.departments.map(d => (
                <Badge key={d.id} variant="secondary" className="bg-slate-100 text-slate-700">
                  <Building2 className="h-3 w-3 mr-1"/>{d.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-1.5">Managed Locations</div>
        <MultiSelect
          options={locOptions}
          value={selected}
          onChange={setSelected}
          placeholder="Select locations…"
          testid={`hod-locs-${row.emp_db_id}`}
        />
      </div>

      <div className="flex items-center justify-between gap-3 pt-1">
        <div className="text-xs text-slate-500">
          {selected.length === 0 ? "No locations selected — HOD scope stays at their primary location only." :
            `Managing ${selected.length} location${selected.length > 1 ? "s" : ""}.`}
        </div>
        <Button size="sm" disabled={!changed || saving} onClick={() => onSave(row, selected)} data-testid={`hod-save-${row.emp_db_id}`} className="bg-emerald-600 hover:bg-emerald-700">
          {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Save className="h-4 w-4 mr-1"/>}
          Save
        </Button>
      </div>
    </div>
  );
}
